/**
 * Чистая логика списка и карточки поездки в кабинете агентства.
 *
 * Здесь нет ни прав, ни запросов: что агентству вообще можно делать, решает `agentStep` с
 * бэка — экран лишь раскладывает это в статус, этап и порядок строк. Вынесено из экранов,
 * потому что именно эти правила хочется проверять тестами, а не рендером.
 */
import type { CabinetTrip } from '../../api/client'

/** Три группы, из которых выводится и пилюля, и сортировка, и фильтры. */
export type TripKind = 'action' | 'wait' | 'done'
export type TripFilter = 'all' | TripKind

type TripLike = Pick<CabinetTrip, 'state' | 'agentStep'> &
  Partial<Pick<CabinetTrip, 'returnedReason' | 'choice' | 'variants' | 'dateFrom'>>

/**
 * Ход за агентством? Это единственный признак, который делит список: `agentStep` непустой —
 * действовать агенту, иначе ждём компанию, а закрытую поездку он держит только как историю.
 */
export function tripKind(t: TripLike): TripKind {
  if (t.agentStep) return 'action'
  return t.state === 'closed' ? 'done' : 'wait'
}

export interface TripStatus {
  kind: TripKind
  /** Ключ i18n для пилюли статуса. */
  pill: string
  /** Ключ i18n для строки «что дальше»; у завершённой её нет. */
  next: string | null
  /** Тон оформления: действие — жёлтый, возврат — красный, ожидание — синий, конец — серый. */
  tone: 'gold' | 'red' | 'blue' | 'slate'
}

/**
 * Статус строки и «что дальше» одним решением: в списке и в карточке они обязаны совпадать,
 * а раньше карточка про состояние не говорила вовсе.
 *
 * Возврат брони сильнее «нужна бронь»: шаг тот же, но агент должен понять, что это не первый
 * заход. Ожидание компании различаем по `choice` — «ждём выбор» и «вариант выбран, ждём
 * подтверждения» для агента разные новости.
 */
export function tripStatus(t: TripLike): TripStatus {
  const kind = tripKind(t)
  if (t.agentStep === 'options')
    return { kind, pill: 'cab.trip.pill.options', next: 'cab.trip.next.options', tone: 'gold' }
  if (t.agentStep === 'booking')
    return t.returnedReason
      ? { kind, pill: 'cab.trip.pill.returned', next: 'cab.trip.next.returned', tone: 'red' }
      : { kind, pill: 'cab.trip.pill.booking', next: 'cab.trip.next.booking', tone: 'gold' }
  if (kind === 'done')
    return { kind, pill: 'cab.trip.pill.done', next: null, tone: 'slate' }
  return {
    kind,
    pill: 'cab.trip.pill.wait',
    next: t.choice ? 'cab.trip.next.chosen' : 'cab.trip.next.waitChoice',
    tone: 'blue',
  }
}

/** Этапы поездки для полосы прогресса. Порядок — порядок маршрута. */
export const TRIP_STAGES = ['received', 'options', 'choice', 'booking', 'done'] as const

/**
 * Текущий этап — индекс в `TRIP_STAGES`; 4 означает «всё пройдено».
 *
 * Выводится из тех же трёх полей, что и статус: точного этапа бэк не отдаёт (`state` — только
 * `in_progress`/`closed`, внутренние стадии ЭДО наружу не идут осознанно). Поэтому «Заявка
 * получена» всегда пройдена: в кабинете поездка появляется, лишь дойдя до шага агентства.
 */
export function tripStage(t: TripLike): number {
  if (t.state === 'closed') return 4
  if (t.agentStep === 'booking') return 3
  if (t.agentStep === 'options' || !t.variants?.length) return 1
  return 2
}

const GROUP: Record<TripKind, number> = { action: 0, wait: 1, done: 2 }

/**
 * Сначала то, где ход за агентством, потом ожидание компании, потом история. Внутри группы —
 * по дате вылета, ближайшие сверху; поездка без даты уходит в конец своей группы, а не в
 * начало (пустая строка наверху выглядела бы как самая срочная).
 */
export function sortTrips<T extends TripLike>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    const g = GROUP[tripKind(a)] - GROUP[tripKind(b)]
    if (g) return g
    const da = a.dateFrom ?? ''
    const db = b.dateFrom ?? ''
    if (!da || !db) return (da ? 0 : 1) - (db ? 0 : 1)
    return da.localeCompare(db)
  })
}

export function filterTrips<T extends TripLike>(rows: readonly T[], f: TripFilter): T[] {
  return f === 'all' ? [...rows] : rows.filter((t) => tripKind(t) === f)
}

/** Счётчики для чипов фильтра — считаем один раз на весь список. */
export function countTrips(rows: readonly TripLike[]): Record<TripFilter, number> {
  const c: Record<TripFilter, number> = { all: rows.length, action: 0, wait: 0, done: 0 }
  for (const t of rows) c[tripKind(t)] += 1
  return c
}
